import {ActionReducerMapBuilder} from '@reduxjs/toolkit'
import {NinchatEvents} from '../types'
import {NinchatState} from 'api/types'
import {accessCreated} from './accessCreated'
import {accessFound} from './accessFound'
import {ack} from './ack'
import {audienceEnqueued} from './audienceEnqueued'
import {audienceRangeCommentContents} from './audienceRangeCommentContents'
import {audienceRegistered} from './audienceRegistered'
import {channelCreated} from './channelCreated'
import {channelFound} from './channelFound'
import {channelJoined} from './channelJoined'
import {channelMemberJoined} from './channelMemberJoined'
import {channelMemberParted} from './channelMemberParted'
import {channelMemberUpdated} from './channelMemberUpdated'
import {channelParted} from './channelParted'
import {channelUpdated} from './channelUpdated'
import {commentCreated} from './commentCreated'
import {dialogueUpdated} from './dialogueUpdated'
import {error} from './error'
import {fileFound} from './fileFound'
import {historyResults} from './historyResults'
import {identityCreated} from './identityCreated'
import {identityDeleted} from './identityDeleted'
import {identityFound} from './identityFound'
import {identityUpdated} from './identityUpdated'
import {jitsiDiscovered} from './jitsiDiscovered'
import {masterKeyCreated} from './masterKeyCreated'
import {masterKeyDeleted} from './masterKeyDeleted'
import {masterKeysFound} from './masterKeysFound'
import {messageReceived} from './messageReceived'
import {messageUpdated} from './messageUpdated'
import {queueActivity} from './queueActivity'
import {queueCreated} from './queueCreated'
import {queueFound} from './queueFound'
import {queueJoined} from './queueJoined'
import {queueMemberJoined} from './queueMemberJoined'
import {queueMemberParted} from './queueMemberParted'
import {queueParted} from './queueParted'
import {queueStatsContents} from './queueStatsContents'
import {queueTranscriptsFound} from './queueTranscriptsFound'
import {queueUpdated} from './queueUpdated'
import {realmFound} from './realmFound'
import {realmJoined} from './realmJoined'
import {realmMemberJoined} from './realmMemberJoined'
import {realmMemberUpdated} from './realmMemberUpdated'
import {realmQueuesFound} from './realmQueuesFound'
import {realmUpdated} from './realmUpdated'
import {sessionCreated} from './sessionCreated'
import {sessionStatusUpdated} from './sessionStatusUpdated'
import {sessionUpdated} from './sessionUpdated'
import {ssoBegun} from './ssoBegun'
import {tagCreated} from './tagCreated'
import {tagDeleted} from './tagDeleted'
import {tagFound} from './tagFound'
import {tagUpdated} from './tagUpdated'
import {tagsFound} from './tagsFound'
import {transcriptContents} from './transcriptContents'
import {transcriptFound} from './transcriptFound'
import {transcriptUpdated} from './transcriptUpdated'
import {userFound} from './userFound'
import {userUpdated} from './userUpdated'


export const ninchatEventExtraReducers = (builder: ActionReducerMapBuilder<NinchatState>): void => {
    builder
        .addCase(channelParted.fulfilled, channelParted.reducer)
        .addCase(channelUpdated.fulfilled, channelUpdated.reducer)
        .addCase(messageReceived.fulfilled, messageReceived.reducer)
        .addCase(sessionCreated.fulfilled, sessionCreated.reducer)
        .addCase(transcriptUpdated.fulfilled, transcriptUpdated.reducer)
        .addCase(channelJoined.fulfilled, channelJoined.reducer)
}

export const ninchatEventExtraActions = {
    [NinchatEvents.NinchatEventType.NinchatChannelPartedEvent]: channelParted,
    [NinchatEvents.NinchatEventType.NinchatChannelUpdatedEvent]: channelUpdated,
    [NinchatEvents.NinchatEventType.NinchatMessageReceivedEvent]: messageReceived,
    [NinchatEvents.NinchatEventType.NinchatSessionCreatedEvent]: sessionCreated,
    [NinchatEvents.NinchatEventType.NinchatTranscriptUpdatedEvent]: transcriptUpdated,
    [NinchatEvents.NinchatEventType.NinchatChannelJoinedEvent]: channelJoined,
}

export const ninchatEventReducers = {
    access_created: accessCreated,
    access_found: accessFound,
    ack,
    audience_enqueued: audienceEnqueued,
    audience_range_comment_contents: audienceRangeCommentContents,
    audience_registered: audienceRegistered,
    channel_created: channelCreated,
    channel_found: channelFound,
    channel_member_joined: channelMemberJoined,
    channel_member_parted: channelMemberParted,
    channel_member_updated: channelMemberUpdated,
    comment_created: commentCreated,
    dialogue_updated: dialogueUpdated,
    error,
    file_found: fileFound,
    history_results: historyResults,
    identity_created: identityCreated,
    identity_deleted: identityDeleted,
    identity_found: identityFound,
    identity_updated: identityUpdated,
    jitsi_discovered: jitsiDiscovered,
    master_key_created: masterKeyCreated,
    master_key_deleted: masterKeyDeleted,
    master_keys_found: masterKeysFound,
    message_updated: messageUpdated,
    permission_groups_listed: (state: NinchatState) => state,
    queue_activity: queueActivity,
    queue_created: queueCreated,
    queue_found: queueFound,
    queue_joined: queueJoined,
    queue_member_joined: queueMemberJoined,
    queue_member_parted: queueMemberParted,
    queue_parted: queueParted,
    queue_stats_contents: queueStatsContents,
    queue_transcripts_found: queueTranscriptsFound,
    queue_updated: queueUpdated,
    realm_found: realmFound,
    realm_joined: realmJoined,
    realm_member_joined: realmMemberJoined,
    realm_member_updated: realmMemberUpdated,
    realm_queues_found: realmQueuesFound,
    realm_updated: realmUpdated,
    session_status_updated: sessionStatusUpdated,
    session_updated: sessionUpdated,
    sso_begun: ssoBegun,
    tag_created: tagCreated,
    tag_deleted: tagDeleted,
    tag_found: tagFound,
    tag_updated: tagUpdated,
    tags_found: tagsFound,
    transcript_contents: transcriptContents,
    transcript_found: transcriptFound,
    user_deleted: (state: NinchatState) => state,
    user_found: userFound,
    user_updated: userUpdated,
}

type Properties<T> = Omit<T, keyof NinchatEvents.NinchatGenericEventProperties>

export const extractProperties = <T extends NinchatEvents.NinchatGenericEventProperties> (event: T): Properties<T> => {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const {
        action_id,
        event: eventType,
        event_id,
        frames,
        ...properties
    } = event
    /* eslint-enable @typescript-eslint/no-unused-vars */

    return properties
}

type Empty = {string?: undefined}

export const isEmpty = (_: Empty): void => undefined // eslint-disable-line @typescript-eslint/no-unused-vars
